/** TanStack Query hooks over the typed endpoints. Pages use these, never `request` directly. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  accountsApi,
  alertsApi,
  authApi,
  deadLettersApi,
  guildsApi,
  healthApi,
  jobsApi,
  mappingsApi,
  settingsApi,
  targetsApi,
} from "./endpoints";
import type {
  Admin,
  ChannelQuery,
  JobCreate,
  LoginRequest,
  MappingBulkRequest,
  MappingCreate,
  MappingPatch,
  MappingQuery,
  SettingsPatch,
  Snowflake,
} from "./types";

export const qk = {
  me: ["auth", "me"] as const,
  health: ["health"] as const,
  accounts: ["accounts"] as const,
  guilds: (q = "") => ["guilds", q] as const,
  channels: (guildId: Snowflake, query: ChannelQuery = {}) =>
    ["guilds", guildId, "channels", query] as const,
  targets: ["targets"] as const,
  mappings: (query: MappingQuery = {}) => ["mappings", query] as const,
  mapping: (id: number) => ["mappings", id] as const,
  effective: (id: number) => ["mappings", id, "effective"] as const,
  globalSettings: ["settings", "global"] as const,
  jobs: ["jobs"] as const,
  job: (id: string) => ["jobs", id] as const,
  alerts: (open?: boolean) => ["alerts", { open }] as const,
  deadLetters: ["dead-letters"] as const,
};

/** Everything that depends on mappings or settings. */
function invalidateConfig(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["mappings"] }),
    qc.invalidateQueries({ queryKey: ["guilds"] }),
    qc.invalidateQueries({ queryKey: ["settings"] }),
    qc.invalidateQueries({ queryKey: qk.targets }),
  ]);
}

/* ---------- auth ---------- */
export function useMe() {
  return useQuery<Admin | null>({
    queryKey: qk.me,
    queryFn: authApi.me,
    retry: false,
    staleTime: 60_000,
  });
}
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => authApi.login(body),
    onSuccess: (res) => {
      if (res.admin) qc.setQueryData(qk.me, res.admin);
    },
  });
}
export function useEnableTotp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => authApi.enableTotp(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      qc.clear();
    },
  });
}

/* ---------- health & monitoring ---------- */
export function useHealth() {
  return useQuery({ queryKey: qk.health, queryFn: healthApi.get, refetchInterval: 5_000 });
}
export function useAlerts(open?: boolean) {
  return useQuery({
    queryKey: qk.alerts(open),
    queryFn: () => alertsApi.list(open),
    refetchInterval: 15_000,
  });
}
export function useDeadLetters(limit = 100) {
  return useQuery({ queryKey: qk.deadLetters, queryFn: () => deadLettersApi.list(limit) });
}
export function useRetryDeadLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (streamId: string) => deadLettersApi.retry(streamId),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: qk.deadLetters }),
        qc.invalidateQueries({ queryKey: qk.health }),
      ]),
  });
}

/* ---------- accounts ---------- */
export function useAccounts() {
  return useQuery({ queryKey: qk.accounts, queryFn: accountsApi.list });
}
export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ label, token }: { label: string; token: string }) =>
      accountsApi.create(label, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.accounts }),
  });
}
export function useReplaceToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, token }: { id: number; token: string }) =>
      accountsApi.replaceToken(id, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.accounts }),
  });
}
export function useTestAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountsApi.test(id),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.accounts }),
  });
}

/* ---------- guilds, channels, targets ---------- */
export function useGuilds(q = "") {
  return useQuery({ queryKey: qk.guilds(q), queryFn: () => guildsApi.list(q || undefined) });
}
export function useChannels(guildId: Snowflake | null, query: ChannelQuery = {}) {
  return useQuery({
    queryKey: qk.channels(guildId ?? "", query),
    queryFn: () => guildsApi.channels(guildId!, query),
    enabled: !!guildId,
    placeholderData: (prev) => prev,
  });
}
export function usePatchGuildSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ guildId, patch }: { guildId: Snowflake; patch: SettingsPatch }) =>
      guildsApi.patchSettings(guildId, patch),
    onSuccess: () => invalidateConfig(qc),
  });
}
export function useTargets() {
  return useQuery({ queryKey: qk.targets, queryFn: targetsApi.list, staleTime: 30_000 });
}

/* ---------- mappings ---------- */
export function useMappings(query: MappingQuery = {}) {
  return useQuery({ queryKey: qk.mappings(query), queryFn: () => mappingsApi.list(query) });
}
/** One mapping (GET /mappings/{id}); disabled for non-integer ids. */
export function useMapping(id: number) {
  return useQuery({
    queryKey: qk.mapping(id),
    queryFn: () => mappingsApi.get(id),
    enabled: Number.isInteger(id),
  });
}
export function useEffective(id: number) {
  return useQuery({ queryKey: qk.effective(id), queryFn: () => mappingsApi.effective(id) });
}
export function useCreateMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MappingCreate) => mappingsApi.create(body),
    onSuccess: () => invalidateConfig(qc),
  });
}
export function usePatchMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: MappingPatch }) =>
      mappingsApi.patch(id, patch),
    onSuccess: () => invalidateConfig(qc),
  });
}
export function useDeleteMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => mappingsApi.remove(id),
    onSuccess: (_data, id) => {
      // Drop the deleted mapping's own queries instead of refetching them into a 404.
      qc.removeQueries({ queryKey: qk.mapping(id) });
      return invalidateConfig(qc);
    },
  });
}
export function useBulkMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MappingBulkRequest) => mappingsApi.bulk(body),
    onSuccess: () => invalidateConfig(qc),
  });
}
export function useTestMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => mappingsApi.test(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.jobs }),
  });
}

/* ---------- settings ---------- */
export function useGlobalSettings() {
  return useQuery({ queryKey: qk.globalSettings, queryFn: settingsApi.global, staleTime: 30_000 });
}
export function usePatchGlobalSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) => settingsApi.patchGlobal(patch),
    onSuccess: () => invalidateConfig(qc),
  });
}

/* ---------- jobs ---------- */
export function useJobs() {
  return useQuery({
    queryKey: qk.jobs,
    queryFn: jobsApi.list,
    // Poll fast while anything is in flight, slowly otherwise.
    refetchInterval: (q) =>
      q.state.data?.items.some((j) => j.status === "queued" || j.status === "running")
        ? 1_500
        : 15_000,
  });
}
export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: JobCreate) => jobsApi.create(body),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: qk.jobs }), invalidateConfig(qc)]),
  });
}
